import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { roundCant } from "@utils/Cant";
import { productTypeFromCabys } from "@utils/Cabys";
import { IVA_TAX_TYPE, TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import { DEFAULT_MEASURE_UNIT } from "@utils/import/resolveProductRows";
import { ParsedLine, ParsedTax } from "./types";

/** Codigo de tarifa "Tarifa 0% (Exento)", usado cuando el XML no declara ninguno. */
const EXEMPT_TAX_RATE_CODE = "01";

/** Descripcion de respaldo: `description` es requerido en los tres modelos de linea. */
const FALLBACK_LINE_DESCRIPTION = "Producto sin detalle";

/**
 * Deduce el `codigo_tarifa` cuando el XML no lo trae.
 *
 * `CodigoTarifaIVA` es opcional en el XSD (los impuestos que no son IVA lo omiten), pero
 * `InvoiceProduct.codigo_tarifa` es requerido. Se invierte la tabla de porcentajes del
 * sistema para no inventar un codigo que no exista en el catalogo.
 */
export const taxRateCodeFromPercentage = (tarifa: number): string => {
    const target = `${tarifa}`;
    const match = Object.entries(TAXES_PERCENTAGES_VALUES).find(([, value]) => value === target);
    return match?.[0] || EXEMPT_TAX_RATE_CODE;
};

/**
 * Impuesto que define la linea. Un comprobante puede declarar varios (IVA mas selectivo de
 * consumo, por ejemplo); el sistema guarda un solo tipo y tarifa por linea, y el que manda es
 * el IVA, que es el que se usa para recalcular.
 */
export const primaryTax = (taxes: ParsedTax[]): ParsedTax | undefined =>
    taxes.find((tax) => tax.codigo === IVA_TAX_TYPE) || taxes[0];

const mapExemption = (tax?: ParsedTax): ProductExemptionDataType | undefined => {
    const exoneracion = tax?.exoneracion;
    if (!exoneracion) return undefined;

    return {
        tipo_exoneracion: exoneracion.tipoDocumento,
        tipoDocumentoOtro: exoneracion.tipoDocumentoOtro,
        numero_documento: exoneracion.numeroDocumento,
        articulo: exoneracion.articulo,
        inciso: exoneracion.inciso,
        institucion: exoneracion.nombreInstitucion,
        nombreInstitucionOtros: exoneracion.nombreInstitucionOtros,
        fecha_emision: exoneracion.fechaEmision,
        monto_impuesto: exoneracion.montoExoneracion,
        porcentaje_compra: `${exoneracion.tarifaExonerada}`,
    };
};

/**
 * Normaliza una `LineaDetalle` del XML a la linea que consume todo el sistema.
 *
 * Sirve igual para `InvoiceProduct`, `CreditnoteProduct` y `DebitnoteProduct`: los tres
 * modelos declaran exactamente los mismos campos de linea, asi que cuando se implementen las
 * notas este mapeo no cambia.
 *
 * Ojo con los dos subtotales: el `SubTotal` del XML ya viene con el descuento restado,
 * mientras que el `subtotal` del sistema es cantidad por precio *antes* del descuento (asi lo
 * recalcula `recalculateProductData` en el slice). El que corresponde es `MontoTotal`.
 */
export const mapParsedLine = (line: ParsedLine, index: number): EDocumentProductDataType => {
    const tax = primaryTax(line.impuestos);
    const descuento = line.descuentos.reduce((total, discount) => total + discount.monto, 0);
    const subtotal = line.montoTotal;
    const tarifa = tax?.tarifa || 0;

    return {
        codigo: line.codigo,
        // `description` y `unidad_medida` son requeridos en el modelo; un XML mal formado que
        // los traiga vacios dejaria la linea ilegible para GraphQL.
        description: line.detalle || line.codigo || FALLBACK_LINE_DESCRIPTION,
        cant: roundCant(line.cantidad),
        price: line.precioUnitario,
        subtotal,
        descuento,
        porcentaje_descuento: subtotal > 0 ? (descuento / subtotal) * 100 : 0,
        impuestos: line.impuestoNeto,
        impuestos_servicio: 0,
        tipo_impuesto: tax?.codigo || IVA_TAX_TYPE,
        porcentaje_impuesto: `${tarifa}`,
        codigo_tarifa: tax?.codigoTarifaIVA || taxRateCodeFromPercentage(tarifa),
        unidad_medida: line.unidadMedida || DEFAULT_MEASURE_UNIT,
        codigo_cabys: line.codigoCabys,
        tipo_producto: productTypeFromCabys(line.codigoCabys),
        // Una linea exonerada al 100% tambien llega con impuesto neto cero: para el sistema
        // ambos casos son lo mismo, no se cobra impuesto.
        exento: line.impuestoNeto <= 0,
        exemption: mapExemption(tax),
        partidaArancelaria: line.partidaArancelaria,
        comision: 0,
        orderIndex: index,
    };
};
